import { FC, useContext, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../../context/notificationContext";
import useI18n from "../../../../../hooks/useTranslations";
import { useApi } from "../../../../../http/api";
import {
  AdminRoofMaterial,
  UpdateRoofMaterialNewImageBody,
  UpdateRoofMaterialRemoveImageBody,
} from "../../../../../http/patrol/types/roofMaterials";
import { useRoofMaterialsApi } from "../../../../../http/patrol/useRoofMaterials";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import { default as useImageUploader } from "../../../../ui/ImageUploader";
import Input from "../../../../ui/Input";
import Numeric from "../../../../ui/Numeric";
import Switch from "../../../../ui/Switch";
import TranslatableField from "../../../../ui/TranslatableField";
import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";

interface UpdateRoofMaterialDetailsProps {
  name: string | null;
  nameIt: string | null;
  nameDe: string | null;
  nameEs: string | null;
  nameFr: string | null;
  order: number | null;
  visibility: VisibilityScopeValue;
  sheetMaterialRequired: boolean;
}

interface RoofMaterialDetailsProps {
  roofMaterial?: AdminRoofMaterial;
  disabled?: boolean;
}

const RoofMaterialDetails: FC<RoofMaterialDetailsProps> = ({ roofMaterial, disabled }: RoofMaterialDetailsProps) => {
  const i18n = useI18n();
  const api = useApi();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const roofMaterialsApi = useRoofMaterialsApi();

  const { component, newImage, removeImage, onSave } = useImageUploader({
    defaultImage: roofMaterial?.image,
    isLoading: !roofMaterial || (!!roofMaterial && !roofMaterial?.id),
    disabled,
  });

  const defaultValues: UpdateRoofMaterialDetailsProps = {
    name: roofMaterial?.name ?? null,
    nameIt: roofMaterial?.nameIt ?? null,
    nameDe: roofMaterial?.nameDe ?? null,
    nameEs: roofMaterial?.nameEs ?? null,
    nameFr: roofMaterial?.nameFr ?? null,
    order: roofMaterial?.order ?? 50,
    visibility: roofMaterial?.visibility ?? "hidden",
    sheetMaterialRequired: roofMaterial?.sheetMaterialRequired ?? false,
  };

  const submit = async (body: UpdateRoofMaterialNewImageBody | UpdateRoofMaterialRemoveImageBody) => {
    try {
      await roofMaterialsApi.updateAdminRoofMaterial(roofMaterial?.id!, body);
      dispatch(showSuccessNotification(i18n.translation));
      onSave();
    } catch (err) {
      return dispatch(showErrorNotification(i18n, err));
    } finally {
      return setIsSubmitting(false);
    }
  };

  const onSubmit = () => async (formData: UpdateRoofMaterialDetailsProps) => {
    setIsSubmitting(true);

    const commonBody = {
      name: formData.name?.trim()!,
      nameIt: formData.nameIt?.trim() ?? null,
      nameDe: formData.nameDe?.trim() ?? null,
      nameEs: formData.nameEs?.trim() ?? null,
      nameFr: formData.nameFr?.trim() ?? null,
      order: formData.order!,
      visibility: formData.visibility,
      sheetMaterialRequired: formData?.sheetMaterialRequired ?? false,
    };

    if (newImage) {
      try {
        const signedUrl = await roofMaterialsApi.generateUploadImageUrl(
          roofMaterial?.id!,
          newImage.name,
          newImage.type,
        );
        const res = await api.uploadFile(signedUrl, newImage);
        if (!res.ok) {
          dispatch(showErrorNotification(i18n, res));
          setIsSubmitting(false);
          return;
        }
        const body: UpdateRoofMaterialNewImageBody = {
          ...commonBody,
          image: newImage.name,
        };
        await submit(body);
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    } else {
      const body: UpdateRoofMaterialRemoveImageBody = {
        ...commonBody,
        removeImage: removeImage,
      };
      await submit(body);
    }
  };

  return (
    <Form<UpdateRoofMaterialDetailsProps>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.save}
      disabled={isSubmitting || disabled}
      onSubmit={onSubmit}
    >
      {({ register, formState: { errors }, control, setValue, reset, watch }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [roofMaterial]);

        useEffect(() => {
          register("nameIt");
          register("nameDe");
          register("nameEs");
          register("nameFr");
        }, [register]);

        const values = watch();

        const sheetMaterialRequired = !!values.sheetMaterialRequired;

        return (
          <FormFieldsContainer imageUploader={component}>
            <FormField label={i18n.translation.common.name} error={errors.name}>
              {({ labelId, isOptional, isInvalid }) => {
                return (
                  <TranslatableField
                    field="name"
                    formValues={values}
                    setFormValue={setValue}
                    dialogTitle={i18n.translation.common.name}
                  >
                    <Input
                      id={labelId}
                      isInvalid={isInvalid}
                      disabled={disabled}
                      {...register("name", {
                        required: !isOptional,
                        minLength: 1,
                      })}
                    />
                  </TranslatableField>
                );
              }}
            </FormField>
            <FormField label={i18n.translation.common.sheetMaterialRequired} isOptional>
              {({ labelId }) => (
                <Switch
                  id={labelId}
                  checked={sheetMaterialRequired}
                  disabled={disabled}
                  onChange={(checked) => setValue("sheetMaterialRequired", checked)}
                />
              )}
            </FormField>
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <VisibilityScope {...field} inputId={labelId} onChange={(value) => setValue("visibility", value)} />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      disabled={disabled}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default RoofMaterialDetails;
